import React, { useEffect, useState } from "react";
import { socket } from "../socket";
import { t, tx } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { PlayerAvatar } from "./PlayerIdentity";

function Results({ role, answers, questions, players, validationView, onValidate, gameId, imposterRoles }) {
  const [localAnswers, setLocalAnswers] = useState(answers);
  const [showHistory, setShowHistory]   = useState(false);
  // Incognito: bet on the identity of the trapped player
  const [bet, setBet]             = useState(null);   // current window
  const [myBet, setMyBet]         = useState(null);
  const [betResult, setBetResult] = useState(null);
  const [betNow, setBetNow]       = useState(Date.now());
  const { playPop } = useSounds();

  useEffect(() => {
    setLocalAnswers(answers);
  }, [answers]);

  useEffect(() => {
    // Keeps validation in sync in real time.
    // `category` present -> scattergories: valid is stored per category ({ catIdx: bool })
    const handleAnswerValidated = ({ questionId, playerId, valid, category }) => {
      setLocalAnswers(prev => {
        const updated = { ...prev };
        if (!updated[playerId]) updated[playerId] = [];
        const idx = updated[playerId].findIndex(a => a.questionId === questionId);
        if (idx !== -1) {
          if (category !== undefined && category !== null) {
            const perCat = { ...(typeof updated[playerId][idx].valid === "object" ? updated[playerId][idx].valid : {}) };
            perCat[category] = valid;
            updated[playerId][idx] = { ...updated[playerId][idx], valid: perCat };
          } else {
            updated[playerId][idx] = { ...updated[playerId][idx], valid };
          }
        }
        return updated;
      });
    };
    socket.on("answerValidated", handleAnswerValidated);
    socket.on("impBetWindow", (w) => { playPop(); setBet(w); setMyBet(null); setBetResult(null); });
    socket.on("impBetAck", ({ suspectId }) => setMyBet(suspectId));
    socket.on("impBetResult", (r) => { playPop(); setBet(null); setBetResult(r); });
    // L'hôte a refermé le résultat du pari : il se referme chez tout le monde.
    socket.on("impBetDismiss", () => setBetResult(null));
    return () => {
      socket.off("answerValidated", handleAnswerValidated);
      socket.off("impBetWindow");
      socket.off("impBetAck");
      socket.off("impBetResult");
      socket.off("impBetDismiss");
    };
  }, [playPop]);

  // Countdown for the betting window
  useEffect(() => {
    if (!bet) return;
    const iv = setInterval(() => setBetNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [bet]);

  const betLeft = bet ? Math.max(0, Math.ceil((bet.duration - (betNow - bet.startedAt)) / 1000)) : 0;
  useCountdownTick(betLeft, { active: !!bet });

  if (!questions.length || !players.length || !validationView) {
    return <div>{t("results.loading")}</div>;
  }

  const { currentQuestion, currentPlayer } = validationView;
  const question = questions[currentQuestion];
  const player = players[currentPlayer];

  if (!question || !player) {
    return <div>{t("results.loading")}</div>;
  }

  // Look up the current player's answer to the current question
  // (indexed by the persistent playerId, which survives reconnections)
  const playerAnswersArr = localAnswers[player.playerId] || [];
  const playerAnswer = playerAnswersArr.find(a => a.questionId === question.id) || {};

  // `imposterRoles[i]` is a playerId, an array (Volunteer) or null
  const roleAt = imposterRoles?.[currentQuestion];
  const wasImposter = Array.isArray(roleAt)
    ? roleAt.includes(player.playerId)
    : roleAt === player.playerId;

  const handleSwitch = (valid, category) => {
    if (role === "host") {
      onValidate(question.id, player.playerId, valid, category);
    }
  };

  const handleNext = () => {
    if (role === "host") {
      socket.emit("nextValidationView", {
        gameId,
        currentQuestion,
        currentPlayer
      });
    }
  };

  const handlePrev = () => {
    if (role === "host") socket.emit("prevValidationView", { gameId });
  };

  // Jump from the history to an already-visited slot
  const handleGoto = (q, p) => {
    if (role !== "host") return;
    socket.emit("gotoValidationView", { gameId, currentQuestion: q, currentPlayer: p });
    setShowHistory(false);
  };

  // Linear position of the validation: the furthest slot reached bounds the
  // browsable history.
  const currentIndex = currentQuestion * players.length + currentPlayer;
  const maxIndex = Math.max(validationView.maxIndex ?? 0, currentIndex);
  const isFirstCell = currentIndex === 0;

  // Validation state of a slot, for the history dots
  const cellStatus = (q, qIdx, p) => {
    const roleOf = imposterRoles?.[qIdx];
    const isImposter = Array.isArray(roleOf)
      ? roleOf.includes(p.playerId)
      : roleOf === p.playerId;
    if (isImposter) return { kind: "imp", label: "🎭" };
    const answer = (localAnswers[p.playerId] || []).find(a => a.questionId === q.id) || {};
    if (q.format === "petit_bac") {
      const perCat = (answer.valid && typeof answer.valid === "object") ? answer.valid : {};
      const total = (q.answers || []).length;
      const ok = Object.values(perCat).filter(Boolean).length;
      return { kind: ok === 0 ? "ko" : ok === total ? "ok" : "partial", label: `${ok}/${total}` };
    }
    return answer.valid ? { kind: "ok", label: "✓" } : { kind: "ko", label: "✗" };
  };

  const myId = players.find(p => p.id === socket.id)?.playerId;

  // Incognito: while bets are open they take over the whole screen — the
  // players' answers would give the trapped player away.
  if (bet) {
    return (
      <div class="Results">
        <div className="imp-bet">
          <span className="imp-reveal-label">{t("results.betLabel", { index: bet.questionIndex + 1 })}</span>
          <h3 className="imp-bet-title">{t("results.betTitle")}</h3>
          <p className="imp-bet-question">{t("results.quotedQuestion", { question: bet.question })}</p>
          <div className="imp-bet-grid">
            {bet.players.filter(p => p.playerId !== myId).map(p => (
              <button key={p.playerId}
                className={`imp-bet-choice${myBet === p.playerId ? " picked" : ""}`}
                disabled={!!myBet}
                onClick={() => socket.emit("impBet", { gameId, questionIndex: bet.questionIndex, suspectId: p.playerId })}>
                <PlayerAvatar player={p} size={44} />
                <span>{p.pseudo}</span>
              </button>
            ))}
          </div>
          <span className="imp-bet-timer">{myBet ? t("results.betRecorded") : `${betLeft}s`}</span>
        </div>
      </div>
    );
  }

  if (betResult) {
    return (
      <div class="Results">
        <div className="imp-reveal">
          <span className="imp-reveal-label">{t("imposter.questionN", { index: betResult.questionIndex + 1 })}</span>
          <h3 className="imp-reveal-title">
            {tx("results.trappedWas", { name: <b>{betResult.trappedPseudo}</b> })}
          </h3>
          {betResult.decoyQuestion && (
            <p className="imp-bet-question">{t("results.trappedSaw", { question: betResult.decoyQuestion })}</p>
          )}
          <div className="imp-reveal-votes">
            {Object.entries(betResult.bets || {}).map(([bettor, suspect]) => {
              const nameOf = pid => players.find(p => p.playerId === pid)?.pseudo || "?";
              return (
                <span key={bettor} className={`imp-reveal-vote${suspect === betResult.trappedId ? " good" : " bad"}`}>
                  {nameOf(bettor)} → {nameOf(suspect)}
                </span>
              );
            })}
          </div>
          <div className="imp-reveal-scores">
            {betResult.players.map(p => (
              <div key={p.playerId} className="imp-reveal-score">
                <PlayerAvatar player={p} size={30} />
                <span>{p.pseudo}</span>
                <b>{p.score}</b>
                {betResult.deltas?.[p.playerId] ? (
                  <em className={betResult.deltas[p.playerId] > 0 ? "up" : "down"}>
                    {betResult.deltas[p.playerId] > 0 ? "+" : ""}{betResult.deltas[p.playerId]}
                  </em>
                ) : null}
              </div>
            ))}
          </div>
          {role === "host" && (
            <button className="btn btn-primary" onClick={() => { setBetResult(null); socket.emit("impBetContinue", { gameId }); }}>{t("results.continueValidation")}</button>
          )}
          {role !== "host" && <p className="imp-waiting">{t("common.waitingForHost")}</p>}
        </div>
      </div>
    );
  }

  return (
    <div class="Results">
      {question.media_url && (
        /\.(jpe?g|png|gif|webp)$/i.test(question.media_url)
          ? <img src={question.media_url} alt="" className="quiz-media" />
          : /\.(mp4|webm)$/i.test(question.media_url)
          ? <video controls src={question.media_url} className="quiz-media" />
          : <audio controls src={question.media_url} />
      )}
      <p>{question.format === "petit_bac" ? t("quiz.pointsPerCategory") : t("quiz.pointsValue", { count: question.difficulty })}</p>
      <p id="text-question">{t("results.questionLine", { index: currentQuestion + 1, question: question.question })}</p>
      {question.format === "petit_bac" ? (
        <div className="pb-validation">
          <p id="text-player">{t("results.playerLine", { name: player.pseudo })}</p>
          {(question.answers || []).map((cat, i) => {
            const perCat = (playerAnswer.valid && typeof playerAnswer.valid === "object") ? playerAnswer.valid : {};
            const catValid = !!perCat[i];
            const catAnswer = Array.isArray(playerAnswer.answer) ? (playerAnswer.answer[i] || "") : "";
            return (
              <div key={i} className={`pb-validation-row${catValid ? " valid" : ""}`}>
                <span className="pb-label">{cat}</span>
                <span className="pb-answer">{catAnswer || <em>—</em>}</span>
                <button
                  className={`pb-toggle${catValid ? " on" : ""}`}
                  disabled={role !== "host"}
                  onClick={() => handleSwitch(!catValid, i)}
                >
                  {catValid ? t("results.catPoint") : t("results.catNoPoint")}
                </button>
              </div>
            );
          })}
        </div>
      ) : question.format === "chrono" ? (
        <div className="chrono-validation">
          <div className="chrono-validation-row">
            <span className="chrono-validation-label">{t("results.correctOrder")}</span>
            <div className="chrono-strip">
              {(question.answers || []).map((url, i) => (
                <div key={url} className="chrono-strip-item">
                  <span>{i + 1}</span><img src={url} alt="" />
                </div>
              ))}
            </div>
          </div>
          <div className="chrono-validation-row">
            <span className="chrono-validation-label">{player.pseudo}</span>
            <div className="chrono-strip">
              {Array.isArray(playerAnswer.answer) && playerAnswer.answer.length > 0
                ? playerAnswer.answer.map((url, i) => {
                    const ok = url === (question.answers || [])[i];
                    return (
                      <div key={url} className={`chrono-strip-item ${ok ? "ok" : "ko"}`}>
                        <span>{i + 1}</span><img src={url} alt="" />
                      </div>
                    );
                  })
                : <em>{t("results.noAnswer")}</em>}
            </div>
          </div>
        </div>
      ) : (
        <>
          {Array.isArray(question.answers) && question.answers.length > 0 && (
            <p id="text-answer">
              {question.type === "multiple"
                ? question.answers[question.correct_answer]
                : question.answers[0]}
            </p>
          )}
          <p id="text-player">{t("results.playerLine", { name: player.pseudo })}</p>
          <p id="text-player-answer">{typeof playerAnswer.answer === "string" ? playerAnswer.answer : ""}</p>
        </>
      )}
      {/* Imposter mode: this player had a different question, their answer is not scored */}
      {wasImposter && (
        <p className="imp-validation-note">
          {t("results.imposterNote")}
          {playerAnswer.decoyQuestion && t("results.sawInstead", { question: playerAnswer.decoyQuestion })}
        </p>
      )}
      {question.format !== "petit_bac" && !wasImposter && (
        <label>
          <input
            type="button"
            disabled={role !== "host"}
            value={playerAnswer.valid ? t("results.true") : t("results.false")}
            onClick={e => handleSwitch(!playerAnswer.valid)}
          />
        </label>
      )}
      <br />
      {role === "host" && (
        <div className="val-nav">
          <span className="val-nav-pos">
            {t("results.navPosition", { q: currentQuestion + 1, qTotal: questions.length, p: currentPlayer + 1, pTotal: players.length })}
          </span>
          <div className="val-nav-btns">
            <button className="btn btn-sm" onClick={handlePrev} disabled={isFirstCell}
              title={t("results.prevTitle")}>
              {t("results.prev")}
            </button>
            <button className="btn btn-sm" onClick={() => setShowHistory(true)}
              title={t("results.historyTitle")}>
              {t("results.historyBtn")}
            </button>
            <button className="btn btn-sm btn-primary" onClick={handleNext}>
              {t("results.next")}
            </button>
          </div>
        </div>
      )}

      {role === "host" && showHistory && (
        <div className="val-history-overlay" onClick={() => setShowHistory(false)}>
          <div className="val-history" onClick={e => e.stopPropagation()}>
            <div className="val-history-head">
              <h3>{t("results.historyHeading")}</h3>
              <button className="val-history-close" onClick={() => setShowHistory(false)} title={t("common.close")}>
                ×
              </button>
            </div>
            <p className="val-history-hint">{t("results.historyHint")}</p>
            <div className="val-history-list">
              {questions.map((q, qi) => {
                const cells = players
                  .map((p, pi) => ({ p, pi }))
                  .filter(({ pi }) => qi * players.length + pi <= maxIndex);
                if (!cells.length) return null;
                return (
                  <div key={q.id ?? qi} className="val-history-q">
                    <span className="val-history-q-title">{qi + 1}. {q.question}</span>
                    <div className="val-history-chips">
                      {cells.map(({ p, pi }) => {
                        const st = cellStatus(q, qi, p);
                        const here = qi === currentQuestion && pi === currentPlayer;
                        return (
                          <button key={p.playerId}
                            className={`val-chip ${st.kind}${here ? " here" : ""}`}
                            onClick={() => handleGoto(qi, pi)}>
                            <span className="val-chip-name">{p.pseudo}</span>
                            <span className="val-chip-state">{st.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Results;